import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  email?: string;
  username?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER';
  role: 'USER' | 'MODERATOR' | 'ADMIN';
  lifetimeConnections?: number;
  profilePhoto?: string;
  subscription?: {
    plan: 'FREE' | 'PREMIUM';
    status: string;
  };
}

interface AuthState {
  user: User | null;
  token: string | null;
  sessionToken: string | null;
  isAuthenticated: boolean;
  isPremium: boolean;
  isAdmin: boolean;

  setAuth: (user: User, token: string) => void;
  setSessionToken: (token: string) => void;
  updateUser: (user: Partial<User>) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      sessionToken: null,
      isAuthenticated: false,
      isPremium: false,
      isAdmin: false,

      setAuth: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
          isPremium: user.subscription?.plan === 'PREMIUM' && user.subscription?.status === 'ACTIVE',
          isAdmin: user.role === 'ADMIN',
        }),

      setSessionToken: (sessionToken) =>
        set({ sessionToken }),

      updateUser: (partial) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...partial } : null,
          isPremium:
            (partial.subscription ?? state.user?.subscription)?.plan === 'PREMIUM' &&
            (partial.subscription ?? state.user?.subscription)?.status === 'ACTIVE',
        })),

      clearAuth: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isPremium: false,
          isAdmin: false,
          // Keep sessionToken for anonymous reconnects
        }),
    }),
    {
      name: 'sw-auth',
      // Token is persisted alongside isAuthenticated so API calls survive
      // page reload. Backend also sets an httpOnly cookie as a backup path.
      partialize: (state) => ({
        sessionToken: state.sessionToken,
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isPremium: state.isPremium,
        isAdmin: state.isAdmin,
      }),
    }
  )
);
